import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

// Optional single button on a toast (e.g. „Освежи" after a version conflict). Clicking it runs
// onClick and closes the toast.
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  count: number;
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Most toasts a user can usefully read at once. Older ones are dropped first.
const MAX_TOASTS = 4;

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Mirror of `toasts` so showToast can dedupe against the live list without
  // taking it as a dependency (which would rebuild the callback on every toast).
  const toastsRef = useRef<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const seqRef = useRef(0);

  const commit = useCallback((next: Toast[]) => {
    toastsRef.current = next;
    setToasts(next);
  }, []);

  const clearTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    clearTimer(id);
    commit(toastsRef.current.filter((toast) => toast.id !== id));
  }, [clearTimer, commit]);

  const scheduleRemoval = useCallback((id: string, duration: number) => {
    clearTimer(id);
    if (duration > 0) {
      timersRef.current.set(id, setTimeout(() => removeToast(id), duration));
    }
  }, [clearTimer, removeToast]);

  const showToast = useCallback((
    message: string,
    type: ToastType = 'success',
    duration = 3000,
    action?: ToastAction
  ) => {
    const lineCount = message.split('\n').length;
    const visibleFor = lineCount > 1
      ? Math.max(duration, Math.min(15000, 4000 + (lineCount - 1) * 700))
      : duration;
    // An identical message is already on screen: bump its counter and restart its
    // timer rather than stacking a copy. Bulk operations that report per row used
    // to push one toast per row and bury the screen.
    const existing = toastsRef.current.find(
      (toast) => toast.message === message && toast.type === type
    );
    if (existing) {
      commit(
        toastsRef.current.map((toast) =>
          toast.id === existing.id ? { ...toast, count: toast.count + 1, action: action ?? toast.action } : toast
        )
      );
      scheduleRemoval(existing.id, visibleFor);
      return;
    }

    // Monotonic id — Math.random().toString(36).substring(7) yielded 4-6 chars and
    // could collide, which duplicated React keys and removed two toasts at once.
    seqRef.current += 1;
    const id = `toast-${seqRef.current}`;
    const next: Toast[] = [...toastsRef.current, { id, message, type, duration, count: 1, action }];

    // Cap the stack, dropping the oldest along with its timer so it can't fire later.
    while (next.length > MAX_TOASTS) {
      const dropped = next.shift();
      if (dropped) clearTimer(dropped.id);
    }

    commit(next);
    scheduleRemoval(id, visibleFor);
  }, [clearTimer, commit, scheduleRemoval]);

  // Never leave a timer running after the provider unmounts.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const getToastIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />;
    }
  };

  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-white border-l-4 border-green-500 shadow-lg';
      case 'error':
        return 'bg-white border-l-4 border-red-500 shadow-lg';
      case 'warning':
        return 'bg-white border-l-4 border-yellow-500 shadow-lg';
      case 'info':
        return 'bg-white border-l-4 border-blue-500 shadow-lg';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Container - positioned above column manager */}
      <div className="fixed bottom-20 right-6 z-[9999] space-y-2 pointer-events-none">
        {toasts.map((toast) => (
          // No index-derived animation delay: removing one toast used to shift the
          // indices of the rest and replay their slide-in, which read as flicker.
          <div
            key={toast.id}
            className={`${getToastStyles(toast.type)} rounded-lg p-4 flex gap-3 min-w-[320px] pointer-events-auto animate-slide-in-right ${
              toast.message.includes('\n') ? 'max-w-[40rem] items-start' : 'max-w-[420px] items-center'
            }`}
          >
            {getToastIcon(toast.type)}
            <p className="text-gray-800 font-medium flex-1 text-sm whitespace-pre-line">{toast.message}</p>
            {toast.action && (
              <button
                type="button"
                onClick={() => {
                  toast.action?.onClick();
                  removeToast(toast.id);
                }}
                className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded px-2 py-1 flex-shrink-0"
              >
                {toast.action.label}
              </button>
            )}
            {toast.count > 1 && (
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 flex-shrink-0">
                &times;{toast.count}
              </span>
            )}
            <button
              onClick={() => removeToast(toast.id)}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label="Close toast"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </ToastContext.Provider>
  );
};
